<script setup lang="ts">
import { AUTH, DASHBOARD } from "@/constants/routes";
import { useMe, useMeAvatar } from "~/api/users/queries";
import type { User } from "~/types/entities";
import { ref } from "vue";

const _config = useRuntimeConfig().public;
const openAccordions = ref<Record<string, boolean>>({});
const isMobileSidebarOpen = ref(false);

const toggleAccordion = (key: string) => {
  openAccordions.value[key] = !openAccordions.value[key];
};

const toggleMobileSidebar = () => {
  isMobileSidebarOpen.value = !isMobileSidebarOpen.value;
};

const closeMobileSidebar = () => {
  isMobileSidebarOpen.value = false;
};

const { data } = useMe();
const { data: dataAvatar } = useMeAvatar();

// Computed ao invés de watchEffect para prevenir loops
const userName = computed(() => {
  if (!data.value) return "";
  const user = data.value.data as User;
  return user.name;
});

const filteredTools = computed(() => {
  return [
    {
      icon: "ion:bar-chart",
      title: "Dashboard",
      href: DASHBOARD.HOME,
    },
    {
      icon: "ion:people",
      title: "Clientes",
      href: DASHBOARD.CUSTOMERS,
    },
    {
      key: "processos",
      icon: "ion:briefcase-outline",
      title: "Processos",
      isAccordion: true,
      children: [
        {
          icon: "hugeicons:justice-scale-01",
          title: "Trabalhista",
          href: DASHBOARD.PROCEEDINGS,
        },
        {
          icon: "ion:receipt-outline",
          title: "Precatório/RPV",
          href: DASHBOARD.PRECATORY_LIST,
        },
        {
          icon: "ion:document-text",
          title: "OAB",
          href: DASHBOARD.PUBLICATIONS,
        },
      ],
    },
    {
      icon: "ion:calendar",
      title: "Agenda",
      href: DASHBOARD.CALENDAR,
    },
    data.value?.data.type !== "commercial"
      ? {
          icon: "ion:cash-outline",
          title: "Financeiro",
          href: DASHBOARD.FINANCE,
        }
      : null,
  ].filter((item): item is NonNullable<typeof item> => Boolean(item));
});

const settings = [
  {
    icon: "ion:person-circle",
    title: "Perfil",
    href: DASHBOARD.PROFILE,
  },
  {
    icon: "ion:briefcase",
    title: "Equipe",
    href: DASHBOARD.TEAM,
  },
  {
    icon: "ion:information-circle",
    title: "Informações",
    href: DASHBOARD.INFO,
  },
].filter((item): item is NonNullable<typeof item> => Boolean(item));
</script>

<template>
  <div class="flex h-screen">
    <!-- Mobile Sidebar Overlay -->
    <div
      v-if="isMobileSidebarOpen"
      class="fixed inset-0 z-50 bg-black bg-opacity-50 md:hidden"
      @click="closeMobileSidebar"
    ></div>

    <!-- Desktop Sidebar -->
    <div
      class="sticky top-0 hidden h-full w-[280px] min-w-[280px] p-6 pr-0 md:block"
    >
      <AppSidebar class="h-full overflow-y-auto">
        <AppSidebarHeader />

        <AppSidebarBody>
          <AppSidebarMenu title="Ferramentas">
            <template v-for="item in filteredTools" :key="item.title">
              <AppSidebarMenuItem
                v-if="!item.isAccordion"
                :icon="item.icon"
                :title="item.title"
                :href="item.href || '#'"
              />
              <div v-else>
                <div
                  class="flex cursor-pointer items-center justify-between rounded-md p-2"
                  @click="toggleAccordion(item.key)"
                >
                  <AppSidebarMenuItem
                    :icon="item.icon"
                    :title="item.title"
                    href="#"
                  />
                  <Icon
                    :name="
                      openAccordions[item.key]
                        ? 'ion:md-arrow-dropup'
                        : 'ion:md-arrow-dropdown'
                    "
                    class="rounded bg-background-alt p-1 text-lg transition-transform duration-200"
                  />
                </div>
                <div v-if="openAccordions[item.key]" class="pl-6">
                  <AppSidebarMenuItem
                    v-for="child in item.children"
                    :key="child.title"
                    :icon="child.icon"
                    :title="child.title"
                    :href="child.href"
                  />
                </div>
              </div>
            </template>
          </AppSidebarMenu>

          <AppSidebarMenu title="Configurações">
            <AppSidebarMenuItem
              v-for="item in settings"
              :key="item.title"
              :icon="item.icon"
              :title="item.title"
              :href="item.href"
            />
          </AppSidebarMenu>
        </AppSidebarBody>

        <AppSidebarFooter>
          <AppSidebarUser
            :avatar="dataAvatar?.data?.acess_link ?? null"
            :name="userName"
            :role="data?.data.occupation ?? ''"
            :href="AUTH.SIGNOUT"
          />
        </AppSidebarFooter>
      </AppSidebar>
    </div>

    <!-- Mobile Sidebar -->
    <div
      :class="[
        'fixed left-0 top-0 z-50 h-screen w-[280px] transform bg-white shadow-xl transition-transform duration-300 ease-in-out md:hidden',
        isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full',
      ]"
    >
      <AppSidebar class="h-full">
        <AppSidebarHeader />

        <AppSidebarBody>
          <AppSidebarMenu title="Ferramentas">
            <template v-for="item in filteredTools" :key="item.title">
              <AppSidebarMenuItem
                v-if="!item.isAccordion"
                :icon="item.icon"
                :title="item.title"
                :href="item.href || '#'"
                @click="closeMobileSidebar"
              />
              <div v-else>
                <div
                  class="flex cursor-pointer items-center justify-between rounded-md p-2"
                  @click="toggleAccordion(item.key)"
                >
                  <AppSidebarMenuItem
                    :icon="item.icon"
                    :title="item.title"
                    href="#"
                  />
                  <Icon
                    :name="
                      openAccordions[item.key]
                        ? 'ion:md-arrow-dropup'
                        : 'ion:md-arrow-dropdown'
                    "
                    class="rounded bg-background-alt p-1 text-lg transition-transform duration-200"
                  />
                </div>
                <div v-if="openAccordions[item.key]" class="pl-6">
                  <AppSidebarMenuItem
                    v-for="child in item.children"
                    :key="child.title"
                    :icon="child.icon"
                    :title="child.title"
                    :href="child.href"
                    @click="closeMobileSidebar"
                  />
                </div>
              </div>
            </template>
          </AppSidebarMenu>

          <AppSidebarMenu title="Configurações">
            <AppSidebarMenuItem
              v-for="item in settings"
              :key="item.title"
              :icon="item.icon"
              :title="item.title"
              :href="item.href"
              @click="closeMobileSidebar"
            />
          </AppSidebarMenu>
        </AppSidebarBody>

        <AppSidebarFooter>
          <AppSidebarUser
            :avatar="dataAvatar?.data?.acess_link ?? null"
            :name="userName"
            :role="data?.data.occupation ?? ''"
            :href="AUTH.SIGNOUT"
          />
        </AppSidebarFooter>
      </AppSidebar>
    </div>

    <!-- Main Content -->
    <div class="flex min-h-0 flex-1 flex-col overflow-y-auto">
      <!-- Mobile Header with Hamburger -->
      <div
        class="flex items-center justify-between border-b bg-white p-4 md:hidden"
      >
        <Button
          variant="ghost"
          size="icon"
          class="h-10 w-10"
          @click="toggleMobileSidebar"
        >
          <Icon name="ion:menu" class="h-6 w-6" />
        </Button>
        <div class="flex items-center gap-3">
          <span class="text-lg font-semibold text-foreground">{{
            userName
          }}</span>
        </div>
      </div>

      <!-- Page Content -->
      <div class="flex-1 p-3 xs:p-4 md:p-6">
        <slot />
      </div>
    </div>
  </div>
</template>

<style scoped></style>
